import React from "react";
import { PageSectionWrapper } from "../../components/PageSectionWrapper";
import DefaultInfoCard from "../../components/InfoCards/DefaultInfoCard";
import { formatCTAS, formatRichText, contentfulToVideoData, contentfulToImageData, filterConfigs } from "../../functions";
import { BANNER_VARIANTS, CONFIGURATION_TYPE_IDS } from "../../constants";
import { PillWithText } from "../../components/Banners/PillImageWithText";
import { VideoBanner } from "../../components/Banners/VideoBanner";
import { ListBanner } from "../../components/Banners/ListBanner";
import { BannerWithHeader } from "../../components/Banners/BannerWithHeader";
import { useState } from "react";

const BANNER_MEDIA_CONFIG = {
	[BANNER_VARIANTS.PILL_IMAGE_WITH_TEXT]: { imgStyle: "object-cover h-[200px] rounded-[1000px] mb-9" },
	[BANNER_VARIANTS.BANNER_WITH_HEADER]:   { imgStyle: "object-cover w-full h-full rounded-t-[32px] md:rounded-r-none md:rounded-l-[32px] min-h-[300px]"},
};

const VERTICAL_ALIGN_CONFIG = {
	top:    { mediaClasses: "mb-auto rounded-t-none", sectionClasses: "!pt-0 !pb-12" },
	bottom: { mediaClasses: "mt-auto rounded-b-none", sectionClasses: "!pt-12 !pb-0" },
	middle: { mediaClasses: "my-auto",                sectionClasses: ""       },
};

const VideoComponent = ({ videoData, poster, mediaStyle, mediaClasses }) => {
	const [isPlaying, setIsPlaying] = useState(false);
	const videoRef = React.useRef(null);

	const handlePlay = () => {
		setIsPlaying(true);
		videoRef.current && videoRef.current.play();
	};

	return (
		<div
			className={`relative rounded-lg overflow-hidden w-full md:w-1/2 ${mediaClasses}`}
			style={mediaStyle}
		>
			<img
				src={poster.imageUrl}
				alt={poster.alt}
				className={`block w-full h-auto rounded-lg${isPlaying ? " invisible" : ""}`}
			/>

			{!isPlaying && (
				<button
					onClick={handlePlay}
					className="absolute inset-0 flex items-center justify-center w-full h-full focus:outline-none"
					aria-label="Play video"
				>
					<span className="bg-white bg-opacity-80 rounded-full p-4">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							className="h-12 w-12 text-white"
							fill="none"
							viewBox="0 0 48 48"
							stroke="#0065F2"
						>
							<polygon points="20,16 34,24 20,32" fill="#0065F2" />
						</svg>
					</span>
				</button>
			)}

			{isPlaying && (
				<video
					ref={videoRef}
					className="absolute inset-0 w-full h-full rounded-lg"
					autoPlay
					controls
				>
					<source src={videoData.url} type="video/mp4" />
					Your browser does not support the video tag.
				</video>
			)}
		</div>
	);
};

export const formatBannerMediaData = (image, bannerMedia, mediaStyle, imgStyle, mediaClasses) => {
	if (bannerMedia && bannerMedia.length > 0) {
		const bannerVideoData = bannerMedia.find(media => media.fields.file.contentType === "video/mp4");
		const videoOverlay = bannerMedia.find(media => media.fields.file.contentType === "image/png");
		if (bannerVideoData && videoOverlay) {
			const videoData = contentfulToVideoData(bannerVideoData);
			const poster = contentfulToImageData(videoOverlay);
			return {
				imageComponent: <VideoComponent videoData={videoData} poster={poster} mediaStyle={mediaStyle} mediaClasses={mediaClasses} />,
			};
		}
	}

	if (!image || !image.fields) return {};

	return {
		imageComponent: (
			<img
				src={image.fields.file?.url || ""}
				alt={image.fields.title || "Banner Image"}
				className={`block w-full ${imgStyle || "rounded-[50px] md:w-1/2 h-auto"} ${mediaClasses}`}
				style={mediaStyle}
				loading="lazy"
			/>
		),
	};
};

const BANNER_CONTENT_COMPONENT_MAP = {
	[BANNER_VARIANTS.DEFAULT]: DefaultInfoCard,
	[BANNER_VARIANTS.PILL_IMAGE_WITH_TEXT]: PillWithText,
	[BANNER_VARIANTS.VIDEO_BANNER]: VideoBanner,
	[BANNER_VARIANTS.LIST_BANNER]: ListBanner,
	[BANNER_VARIANTS.BANNER_WITH_HEADER]: BannerWithHeader	
};
export const BannerPageSection = ({
	headerText,
	subtitle,
	description,
	ctas,
	variant,
	siteStatus,
	image,
	bannerVariant,
	bannerMedia,
	imageOnLeft = false,
	formattedSectionStyles = {},
	configurations = [],
	isStatic = false,
	...rest
}) => {
	const formattedDescription = formatRichText(description, siteStatus, [], {}, isStatic);

	const formattedCTAs = formatCTAS(ctas, siteStatus, isStatic);

	const bannerMediaConfig = filterConfigs(configurations, CONFIGURATION_TYPE_IDS.BANNER_MEDIA)[0];
	const { mediaClasses, sectionClasses } = VERTICAL_ALIGN_CONFIG[bannerMediaConfig?.verticalAlign] ?? VERTICAL_ALIGN_CONFIG.middle;
	const { imgStyle = "", aspectRatio } = BANNER_MEDIA_CONFIG[bannerVariant] ?? {};
	const mediaStyle = {
		maxWidth: bannerMediaConfig?.maxWidth ?? "100%",
		...(aspectRatio && { aspectRatio }),
		...(bannerMediaConfig?.width && { width: bannerMediaConfig.width }),
	};

	const { imageComponent } = formatBannerMediaData(image, bannerMedia, mediaStyle, imgStyle, mediaClasses);
	const BannerContentComponent = BANNER_CONTENT_COMPONENT_MAP[bannerVariant] || DefaultInfoCard;

	const bannerSectionStyling = {
		...formattedSectionStyles,
		cardStyling: {
			backgroundColor: formattedSectionStyles.wrapperColor || formattedSectionStyles.inverseColor,
		},
	};

	return (
		<PageSectionWrapper variant={variant} parsedClasses={sectionClasses}>
			<BannerContentComponent
				{...rest}
				headerText={headerText}
				subtitle={subtitle}
				content={formattedDescription}
				ctas={formattedCTAs}
				isEven={!imageOnLeft}
				imageComponent={imageComponent}
				formattedSectionStyles={bannerSectionStyling}
				imageWrapperStyling={bannerVariant === BANNER_VARIANTS.DEFAULT ?"md:basis-6/12":""}
			/>
		</PageSectionWrapper>
	);
};
